import { useEffect, useReducer, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import useCountries from '../../hooks/useCountries';
import {
  APPLICANT_TYPES,
  APPLICATION_TYPE,
  CorporateAccountType,
} from '../../util/constants';
import { findCountryByCode } from '../../util/helpers/countries';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import Paragraph from '../../components/Paragraph/Paragraph';
import AccountNameForm from '../../components/Forms/AccountNameForm';
import AddressForm from '../../components/Forms/AddressForm';
import AccountDobForm from '../../components/Forms/AccountDobForm';
import SsnForm from '../../components/Forms/SsnForm';
import IdentityCountryForm from '../../components/Forms/IdentityCountryForm';
import { extractValue, hashedMask } from '../../util/helpers/ssnHelper';
import {
  extractValue as extractEin,
  hashedMask as hashedEin,
} from '../../util/helpers/einHelper';
import ReviewData from '../../components/Review/ReviewData';
import Name from '../../components/Name';
import AddressReviewFormat, {
  CountryName,
} from '../../components/AddressReviewFormat';
import Birthday from '../../components/Birthday';
import Modal from '../../components/Modal';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE } from '../../constants/sessionStorage';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import TrustNameForm from '../../components/Forms/TrustNameForm';
import BusinessEinForm from '../../components/Forms/BusinessEinForm';
import { useApplicantData } from '../../hooks/useApplicantData';
import PhoneFormat from '../../components/Phone';
import PhoneNumberForm from '../../components/Forms/PhoneNumberForm';
import themeColors from '../../assets/styles/themeColors';
import MissingInfo from './MisingInfo';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 0 1.5rem 0 1.5rem;

  @media only screen and (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const reducer = (state, action) => ({
  ...state,
  ...action.payload,
});

const initialState = {
  isAddressOpen: false,
  isNameOpen: false,
  isSsnOpen: false,
  isDobOpen: false,
  isCountryOpen: false,
  isPhoneOpen: false,
  isMaritalOpen: false,
  isBusinessNameOpen: false,
  isSubmitSuccessful: false,
  isFundingOpen: false,
  isIraFundingOpen: false,
};

const MoreInfo = ({ secondOwner, isSalesAssisted, setHasMissingInfo }) => {
  const [
    {
      isAddressOpen,
      isNameOpen,
      isSsnOpen,
      isDobOpen,
      isCountryOpen,
      isPhoneOpen,
    },
    dispatch,
  ] = useReducer(reducer, initialState);
  const { t } = useTranslation();
  const countries = useCountries();
  const Application = useSelector(selectApplication);
  const [isTrustName, setIsTrustName] = useState(false);
  const [isEinOpen, setEinOpen] = useState(false);
  const { ApplicationType } = Application;
  const AccountType = retrieveFromSessionStorage(ACCOUNT_TYPE);
  const ApplicantData = useApplicantData(APPLICANT_TYPES.BUSINESS);
  const [eventType, setEventType] = useState('NoForm');

  const applicantTypeString = () => {
    if (APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[4]) {
      return APPLICANT_TYPES?.SECONDARY;
    }
    if (APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[5]) {
      return APPLICANT_TYPES?.MINOR;
    }
    if (
      APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[2] &&
      AccountType === CorporateAccountType[4]
    ) {
      return APPLICANT_TYPES?.BUSINESS;
    }
  };

  const {
    PersonAddress,
    PersonBirthday,
    PersonCountry,
    PersonName,
    PersonSsn,
    Phone,
    Name: TrustName,
  } = secondOwner;

  const isSsnMissing = () => {
    if (PersonCountry?.CountryIso3Code === 'USA' && !PersonSsn?.Ssn) {
      return true;
    }

    return false;
  };

  const getSsnValue = () => {
    if (isSsnMissing()) {
      return <MissingInfo />;
    }

    if (PersonSsn?.Ssn) {
      return hashedMask(extractValue(PersonSsn?.Ssn));
    }

    return 'N/A';
  };

  const formatEinNumber = (ein) => {
    if (
      !ein &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE[2] &&
      AccountType === CorporateAccountType[4]
    ) {
      return <MissingInfo />;
    }
    if (ein) {
      const formatedEin = hashedEin(extractEin(ein));
      return formatedEin;
    }
  };

  const isNameMissing = () => {
    if (PersonName?.FirstName === '' && PersonName?.LastName === '') {
      return true;
    }
    return false;
  };

  const isAddressMissing = () => {
    if (
      PersonAddress?.CountryIso3Code === '' &&
      PersonAddress?.City === '' &&
      PersonAddress?.State === ''
    ) {
      return true;
    }
    return false;
  };

  const isBirthdayMissing = () => {
    if (!PersonBirthday?.DateOfBirth) {
      return true;
    }
    return false;
  };

  const isPhoneMissing = () => {
    if (
      !Phone?.Iso3CountryCode &&
      !Phone?.LocalNumber &&
      !Phone?.StateCode &&
      !Phone?.AreaCode
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!isEmpty(secondOwner)) {
      if (
        isNameMissing() ||
        isSsnMissing() ||
        isAddressMissing() ||
        isBirthdayMissing() ||
        isPhoneMissing()
      ) {
        setHasMissingInfo(true);
        return;
      }
      return setHasMissingInfo(false);
    }
  }, [secondOwner]); //eslint-disable-line

  return (
    <>
      <Grid>
        {APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[2] &&
          AccountType === CorporateAccountType[4] && (
            <ReviewData
              title={t('verifyReview.trustName')}
              onEdit={() => setIsTrustName(true)}
              value={!TrustName ? <MissingInfo /> : TrustName}
            />
          )}

        {APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[2] &&
          AccountType === CorporateAccountType[4] && (
            <ReviewData
              title={t('verifyReview.ein')}
              onEdit={() => setEinOpen(true)}
              value={
                formatEinNumber(getApplicantData(ApplicantData, 'EIN')) || 'N/A'
              }
            />
          )}

        {!isEmpty(PersonName) && (
          <ReviewData
            title={t('standardReview.yourName')}
            onEdit={() => dispatch({ payload: { isNameOpen: true } })}
            dataCy="button-edit-name"
          >
            <Name {...PersonName} isSalesAssisted={isSalesAssisted} />
          </ReviewData>
        )}
        {!isEmpty(PersonAddress) && (
          <ReviewData
            title={t('standardReview.address')}
            onEdit={() => dispatch({ payload: { isAddressOpen: true } })}
            dataCy="button-edit-address"
          >
            <AddressReviewFormat
              {...PersonAddress}
              isSalesAssisted={isSalesAssisted}
            />
          </ReviewData>
        )}
        {!isEmpty(PersonBirthday) && (
          <ReviewData
            title={t('standardReview.yourBirthday')}
            onEdit={() => dispatch({ payload: { isDobOpen: true } })}
            dataCy="button-edit-birthday"
          >
            <Birthday {...PersonBirthday} isSalesAssisted={isSalesAssisted} />
          </ReviewData>
        )}
        {PersonCountry?.CountryIso3Code === 'USA' && (
          <ReviewData
            title={t('standardReview.yourSsn')}
            onEdit={() => dispatch({ payload: { isSsnOpen: true } })}
            value={getSsnValue()}
            dataCy="button-edit-ssn"
          />
        )}
        {PersonCountry?.CountryIso3Code && (
          <ReviewData
            title={t('standardReview.yourCitizenship')}
            onEdit={() => dispatch({ payload: { isCountryOpen: true } })}
            dataCy="button-edit-citizenship"
          >
            <Paragraph color={themeColors.colorTextXLLight} variant="normal">
              <CountryName>
                {findCountryByCode(countries, PersonCountry?.CountryIso3Code)}
              </CountryName>
            </Paragraph>
          </ReviewData>
        )}
        {!isEmpty(Phone) && (
          <ReviewData
            title={t('standardReview.yourPhone')}
            onEdit={() => dispatch({ payload: { isPhoneOpen: true } })}
            dataCy="button-edit-phone-number"
          >
            <PhoneFormat Phone={Phone} isSalesAssisted={isSalesAssisted} />
          </ReviewData>
        )}
      </Grid>
      <Modal
        isOpen={isNameOpen}
        onClose={() => {
          dispatch({ payload: { isNameOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.yourName')}
      >
        <AccountNameForm
          onModalClose={() => {
            dispatch({ payload: { isNameOpen: false } });
            setEventType('NoForm');
          }}
          applicantType={applicantTypeString()}
          formId="name_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isAddressOpen}
        onClose={() => {
          dispatch({ payload: { isAddressOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.address')}
      >
        <AddressForm
          applicantType={applicantTypeString()}
          onModalClose={() => {
            dispatch({ payload: { isAddressOpen: false } });
            setEventType('NoForm');
          }}
          formId="address_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isDobOpen}
        onClose={() => {
          dispatch({ payload: { isDobOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.yourBirthday')}
      >
        <AccountDobForm
          applicantType={applicantTypeString()}
          onModalClose={() => {
            dispatch({ payload: { isDobOpen: false } });
            setEventType('NoForm');
          }}
          formId="dob_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isSsnOpen}
        onClose={() => {
          dispatch({ payload: { isSsnOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.yourSsn')}
      >
        <SsnForm
          applicantType={applicantTypeString()}
          onModalClose={() => {
            dispatch({ payload: { isSsnOpen: false } });
            setEventType('NoForm');
          }}
          formId="ssn_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isCountryOpen}
        onClose={() => {
          dispatch({ payload: { isCountryOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.yourCitizenship')}
      >
        <IdentityCountryForm
          applicantType={applicantTypeString()}
          onModalClose={() => {
            dispatch({ payload: { isCountryOpen: false } });
            setEventType('NoForm');
          }}
          formId="identity_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isPhoneOpen}
        onClose={() => {
          dispatch({ payload: { isPhoneOpen: false } });
          setEventType('NoForm');
        }}
        label={t('standardReview.yourPhone')}
      >
        <PhoneNumberForm
          applicantType={applicantTypeString()}
          onModalClose={() => {
            dispatch({ payload: { isPhoneOpen: false } });
            setEventType('NoForm');
          }}
          formId="phone_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isTrustName}
        onClose={() => {
          setIsTrustName(false);
          setEventType('NoForm');
        }}
        label={t('verifyReview.businessNameLabel')}
      >
        <TrustNameForm
          applicantType={APPLICANT_TYPES.BUSINESS}
          onModalClose={() => {
            setIsTrustName(false);
            setEventType('NoForm');
          }}
          formId="trustName_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
      <Modal
        isOpen={isEinOpen}
        onClose={() => {
          setEinOpen(false);
          setEventType('NoForm');
        }}
        label={t('verifyReview.businessEinLabel')}
      >
        <BusinessEinForm
          applicantType={APPLICANT_TYPES.BUSINESS}
          onModalClose={() => {
            setEinOpen(false);
            setEventType('NoForm');
          }}
          formId="businessEin_form"
          setEventType={setEventType}
          eventType={eventType}
        />
      </Modal>
    </>
  );
};

MoreInfo.propTypes = {
  secondOwner: PropTypes.func,
  isSalesAssisted: PropTypes.bool,
  setHasMissingInfo: PropTypes.func,
};

export default MoreInfo;
